import { format, parseISO } from 'date-fns';
import { enUS } from 'date-fns/locale';
import Head from 'next/head';
import { getFullImageUrl } from 'utils/image';
import { getStaffFullName } from 'utils/staff';
import config from 'config';
import type { DoctorProfileEntry } from '.';
import type { ReviewStats } from 'types/Review';

type Props = {
  entry: DoctorProfileEntry;
  reviewStats?: ReviewStats;
  review?: Record<string, string>;
  locale: string;
};

const StructuredData = ({
  entry,
  locale,
  reviewStats,
  review,
}: Props): JSX.Element => {
  const url = `https://${config.app.domains.proxy.production}/${locale}/${entry.slug}`;
  const { clinic, city } = entry;
  const { country } = city;
  const clinicImageUrl = clinic?.photos.items?.[0]?.url || '';
  const clinicAddress = clinic?.address || '';
  const clinicName = clinic?.name || '';

  const getImageData = (imageUrl: string): string[] | string => {
    if (!imageUrl) {
      return '';
    }

    const fullImageUrl = getFullImageUrl(imageUrl);

    return [
      `${fullImageUrl}?fit=fill&w=675&h=1200`,
      `${fullImageUrl}?fit=fill&w=825&h=1200`,
      `${fullImageUrl}?fit=fill&w=890&h=890`,
    ];
  };

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Physician',
    // -- We provide 3 high-res images with 16x9, 4x3 and 1x1 as per
    // -- https://support.google.com/webmasters/thread/29138994?hl=en
    image: getImageData(entry?.doctorImage?.url),
    '@id': url,
    url,
    name: getStaffFullName(entry, locale),
    address: {
      '@type': 'PostalAddress',
      streetAddress: clinicAddress,
      addressLocality: city.name,
      addressCountry: country.acronym,
    },
    hospitalAffiliation: {
      '@type': 'Hospital',
      name: clinicName,
      image: getImageData(clinicImageUrl),
      address: {
        '@type': 'PostalAddress',
        streetAddress: clinicAddress,
        addressLocality: city.name,
        addressCountry: country.acronym,
      },
    },
    memberOf: {
      '@type': 'MedicalClinic',
      name: clinicName,
      image: getImageData(clinicImageUrl),
      address: {
        '@type': 'PostalAddress',
        streetAddress: clinicAddress,
        addressLocality: city.name,
        addressCountry: country.acronym,
      },
    },
    availableService: {
      '@type': 'MedicalProcedure',
      name: entry.staffTreatment.items[0]?.treatment?.name || '',
    },
    description: entry.metaDescription,
    isAcceptingNewPatients: true,
  };

  const { avg_rating, total } = reviewStats || {};
  if (avg_rating && total) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    structuredData.aggregateRating = {
      '@type': 'aggregateRating',
      ratingValue: avg_rating,
      reviewCount: total,
    };
  }

  const { overall_rating, description, created_at, customer_name } =
    review || {};

  if (overall_rating && description && created_at && customer_name) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    structuredData.review = {
      '@type': 'Review',
      reviewRating: {
        '@type': 'Rating',
        ratingValue: overall_rating,
      },
      reviewBody: description,
      datePublished: format(parseISO(created_at), 'yyyy-MM-dd', {
        locale: enUS,
      }),
      author: {
        '@type': 'Person',
        name: customer_name,
      },
    };
  }

  return (
    <Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
    </Head>
  );
};

export default StructuredData;
